import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FileSignature, ShieldCheck } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

export default function ParentAccept() {
  const { token } = useParams();
  const nav = useNavigate();
  const [invite, setInvite] = useState(null);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({ parent_name: "", password: "", signature: "" });
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/kids/invite/${token}`).then((r) => setInvite(r.data)).catch(() => setErr("Invite not found or expired"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!agree) { toast.error("Please accept the consent"); return; }
    if (!form.parent_name.trim() || !form.password || !form.signature.trim()) {
      toast.error("All fields required"); return;
    }
    setBusy(true);
    try {
      await api.post(`/kids/parent-accept/${token}`, form);
      toast.success("Consent recorded. You can sign in now as the parent account.");
      nav("/login");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not accept");
    } finally { setBusy(false); }
  };

  if (err) return <div data-testid="parent-accept-error" className="pt-32 text-center text-red-400">{err}</div>;
  if (!invite) return <div className="pt-32 text-center text-white/60">Loading invite…</div>;

  return (
    <div data-testid="parent-accept-page" className="min-h-screen pt-32 pb-20 px-4 flex items-start justify-center">
      <div className="tv-glass rounded-2xl w-full max-w-2xl p-6 md:p-8">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck className="w-6 h-6 text-tv-brand" />
          <div className="text-xs uppercase tracking-[0.3em] text-tv-accent font-bold">Parent / Guardian Consent</div>
        </div>
        <h1 className="font-display font-black text-3xl md:text-4xl text-white tracking-tight">
          You're being asked to sponsor <span className="text-tv-brand">{invite.kid_name}</span> on Tap In Kids.
        </h1>
        <p className="text-white/70 mt-3 text-sm">
          By completing this form you: (1) create your monitoring account, (2) consent to your child
          creating content on the Kids Network, and (3) agree to review every upload before it goes live.
          You will retain the ability to revoke consent at any time.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-white/60 font-semibold">Parent / Guardian full name</label>
            <input
              data-testid="parent-name-input"
              value={form.parent_name}
              onChange={(e) => setForm({ ...form, parent_name: e.target.value })}
              className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white outline-none focus:border-tv-brand"
              required
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/60 font-semibold">Email (your monitoring account)</label>
            <input
              disabled
              value={invite.parent_email}
              className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white/80 outline-none opacity-80"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/60 font-semibold">Choose a password (8+ chars)</label>
            <input
              data-testid="parent-password-input"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white outline-none focus:border-tv-brand"
              minLength={8}
              required
            />
          </div>
          <div className="tv-glass rounded-lg p-4 border border-white/10">
            <div className="flex items-center gap-2 mb-2 text-tv-brand font-semibold text-sm">
              <FileSignature className="w-4 h-4" /> Consent statement
            </div>
            <p className="text-white/75 text-xs leading-relaxed">
              I am the parent or legal guardian of the minor above. I consent to their participation as a creator
              on the Tap In Kids Network. I will actively monitor their uploads via my parent account. I understand
              every upload requires my review and platform moderation before it goes live. I release the platform
              and co-participants from liability for my child's submissions.
            </p>
          </div>
          <label className="flex items-start gap-2">
            <input
              data-testid="parent-agree-checkbox"
              type="checkbox"
              checked={agree}
              onChange={(e) => setAgree(e.target.checked)}
              className="mt-1"
            />
            <span className="text-white/85 text-sm">I agree to the consent statement above.</span>
          </label>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/60 font-semibold">Type your full legal name as signature</label>
            <input
              data-testid="parent-signature-input"
              value={form.signature}
              onChange={(e) => setForm({ ...form, signature: e.target.value })}
              placeholder="e.g. Alex Johnson"
              className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white outline-none focus:border-tv-brand"
              required
            />
          </div>
          <button
            data-testid="parent-accept-submit"
            type="submit"
            disabled={busy}
            className="w-full bg-tv-accent text-black font-bold py-3 rounded-full disabled:opacity-60"
          >
            {busy ? "Saving consent…" : "Create Parent Account & Sign"}
          </button>
        </form>
      </div>
    </div>
  );
}
